import type { OfflinePassScan, PassScan, SeasonPass } from "indexedDb/entities";

export type MatchEvent = { slug: string; start: string | null; end: string | null };

/** Mirrors liveto-api ENTRY_WINDOW_HOURS — keep in sync. */
const ENTRY_WINDOW_MS = 3 * 60 * 60 * 1000;

/** The match whose entry window covers `now`, or null.
 *
 * Window = [start - 3h, end]; overlapping windows split at the later match's
 * opening (doubleheader: attribution flips to match B the moment
 * start_B - 3h passes). Identical rule to liveto-api resolve_match_event —
 * keep them in sync. */
export function attributeMatch(matches: MatchEvent[], now: Date = new Date()): MatchEvent | null {
	const opened = matches
		.filter(m => m.start && m.end && new Date(m.start).getTime() - ENTRY_WINDOW_MS <= now.getTime())
		.sort(
			(a, b) =>
				new Date(a.start!).getTime() - new Date(b.start!).getTime() ||
				a.slug.localeCompare(b.slug),
		);
	const candidate = opened[opened.length - 1];
	return candidate && now.getTime() <= new Date(candidate.end!).getTime() ? candidate : null;
}

export type PassScanOutcome = "accept" | "reject" | "prompt";
export type PassScanReason =
	| "not_found"
	| "inactive"
	| "expired"
	| "cancelled"
	| "renewed"
	| "rescan"
	| null;

/** Once-per-current-day season pass rule, pure so it's testable.
 * A rescan is never a hard reject: the caller prompts the operator. */
export function evaluatePassScan(
	pass: SeasonPass | null,
	todaysScans: (PassScan | OfflinePassScan)[],
	now: Date = new Date(),
): { outcome: PassScanOutcome; reason: PassScanReason } {
	if (!pass) {
		return { outcome: "reject", reason: "not_found" };
	}
	if (pass.status === "inactive") {
		return { outcome: "reject", reason: "inactive" };
	}
	if (pass.status === "cancelled") {
		return { outcome: "reject", reason: "cancelled" };
	}
	if (pass.status === "renewed") {
		return { outcome: "reject", reason: "renewed" };
	}
	if (pass.status === "expired" || (pass.valid_until && new Date(pass.valid_until) < now)) {
		return { outcome: "reject", reason: "expired" };
	}
	if (todaysScans.length > 0) {
		return { outcome: "prompt", reason: "rescan" };
	}
	return { outcome: "accept", reason: null };
}
